import type {BuilderParams} from "../../themes/builder-constants";
import type React from "react";

import {InputGroup, Kbd, Label, ListBox, Popover, Typography} from "@heroui/react";
import {ExpandIcon, ThemeIcon} from "@project/icons-web";

import {useKeyPress} from "../../hooks/use-key-press";
import {THEME_PRESETS} from "../../themes/builder-constants";

import {useThemeBuilder} from "./theme-builder-context";

/* ------------------------------------------------------------------------------------------------
 * findMatchingPreset — returns the preset id if all params match, else undefined
 * ------------------------------------------------------------------------------------------------ */
function findMatchingPreset(params: BuilderParams): string | undefined {
  return THEME_PRESETS.find(
    (p) =>
      p.hue === params.hue &&
      p.chroma === params.chroma &&
      p.lightness === params.lightness &&
      p.base === params.base &&
      p.radius === params.radius &&
      p.formRadius === params.formRadius &&
      p.fontFamily === params.fontFamily,
  )?.id;
}

/* ------------------------------------------------------------------------------------------------
 * ThemePopover — preset theme selector matching the reference theme-popover.tsx
 * ------------------------------------------------------------------------------------------------ */
export function ThemePopover() {
  const {loadPreset, params} = useThemeBuilder();
  const currentPresetId = findMatchingPreset(params);
  const currentPreset = currentPresetId
    ? THEME_PRESETS.find((p) => p.id === currentPresetId)
    : undefined;

  function handleSelectionChange(keys: Set<React.Key> | "all") {
    if (keys === "all") return;
    const id = [...keys][0] as string | undefined;

    if (!id) return;
    const preset = THEME_PRESETS.find((p) => p.id === id);

    if (preset) loadPreset(preset);
  }

  function randomize() {
    const preset = THEME_PRESETS[Math.floor(Math.random() * THEME_PRESETS.length)];

    if (preset) loadPreset(preset);
  }

  useKeyPress("t", randomize);

  return (
    <Popover>
      <div className="flex flex-col gap-1">
        <Label>Theme</Label>
        <Popover.Trigger>
          <InputGroup className="w-40 cursor-pointer">
            <InputGroup.Prefix className="w-10">
              <ThemeIcon className="size-4" />
            </InputGroup.Prefix>
            <InputGroup.Input
              readOnly
              className="max-w-20 cursor-pointer capitalize"
              id="theme"
              name="theme"
              value={currentPreset?.label ?? "Custom"}
            />
            <InputGroup.Suffix className="w-10">
              <ExpandIcon className="size-3" />
            </InputGroup.Suffix>
          </InputGroup>
        </Popover.Trigger>
      </div>
      <Popover.Content className="w-57 rounded-3xl" placement="top">
        <Popover.Dialog className="p-4">
          <ListBox
            aria-label="Theme"
            className="grid grid-cols-4 gap-3"
            layout="grid"
            selectedKeys={currentPresetId ? new Set([currentPresetId]) : new Set()}
            selectionMode="single"
            onSelectionChange={handleSelectionChange}
          >
            {THEME_PRESETS.map((preset) => (
              <ListBox.Item
                key={preset.id}
                id={preset.id}
                textValue={preset.id}
                className="group relative flex w-10 flex-col items-center justify-center gap-1.5 p-0 hover:bg-transparent data-[hovered=true]:bg-transparent"
              >
                <img
                  alt={preset.label}
                  src={preset.image}
                  width={36}
                  height={36}
                  className="bg-surface group-data-[selected=true]:ring-accent group-data-[selected=true]:ring-offset-surface size-9 rounded-full group-data-[selected=true]:ring-2 group-data-[selected=true]:ring-offset-2"
                />
                <span className="text-muted group-data-[selected=true]:text-foreground text-[10px] font-medium capitalize">
                  {preset.label}
                </span>
              </ListBox.Item>
            ))}
          </ListBox>
          <Typography className="text-muted mt-5 text-xs">
            Press{" "}
            <Kbd>
              <Kbd.Content>T</Kbd.Content>
            </Kbd>{" "}
            to pick random
          </Typography>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}
